// @ts-check
/* eslint-env browser */

import DOMPurify from 'dompurify'
import { render, html } from 'uhtml'
import '../status-tag.js'
import { reactiveStore } from '../../reactive-store.js'
import { getAllowedEntityPropertiesForEntity, getEntityType } from '../../../../utils/entity-properties.js'

/**
 * ⚠️ SYNC REQUIRED: Tool SVG icons for inline use with labels (12px to match text size)
 * These SVG path data strings are DUPLICATED in src/components/map/edit-controls/macro.html
 */
const ICON_ADD_POINT = html`<span class="tool-badge"><svg aria-hidden="true" viewBox="0 -960 960 960">
  <path d="M440-400h80v-120h120v-80H520v-120h-80v120H320v80h120v120Zm40 214q122-112 181-203.5T720-552q0-109-69.5-178.5T480-800q-101 0-170.5 69.5T240-552q0 71 59 162.5T480-186Zm0 106Q319-217 239.5-334.5T160-552q0-150 96.5-239T480-880q127 0 223.5 89T800-552q0 100-79.5 217.5T480-80Zm0-480Z"/>
</svg><span>Add tree</span></span>`

const ICON_ADD_FREEFORM = html`<span class="tool-badge"><svg aria-hidden="true" viewBox="0 -960 960 960">
  <path d="M160-120v-170l527-526q12-12 27-18t30-6q16 0 30.5 6t25.5 18l56 56q12 11 18 25.5t6 30.5q0 15-6 30t-18 27L330-120H160Zm80-80h56l393-392-28-29-29-28-392 393v56Zm560-503-57-57 57 57Zm-139 82-29-28 57 57-28-29ZM560-120q74 0 137-37t63-103q0-36-19-62t-51-45l-59 59q23 10 36 22t13 26q0 23-36.5 41.5T560-200q-17 0-28.5 11.5T520-160q0 17 11.5 28.5T560-120ZM183-426l60-60q-20-8-31.5-16.5T200-520q0-12 18-24t76-37q88-38 117-69t29-70q0-55-44-87.5T280-840q-45 0-80.5 16T145-785q-11 13-9 29t15 26q13 11 29 9t27-13q14-14 31-20t42-6q41 0 60.5 12t19.5 28q0 14-17.5 25.5T262-654q-80 35-111 63.5T120-520q0 32 17 54.5t46 39.5Z"/>
</svg><span>Add (freeform)</span></span>`

const ICON_MAGIC_WAND = html`<span class="tool-badge"><svg aria-hidden="true" viewBox="0 -960 960 960">
  <path d="m176-120-56-56 301-302-181-45 198-123-17-234 179 151 216-88-87 217 151 178-234-16-124 198-45-181-301 301Zm24-520-80-80 80-80 80 80-80 80Zm355 197 48-79 93 7-60-71 35-86-86 35-71-59 7 92-79 49 90 22 23 90Zm165 323-80-80 80-80 80 80-80 80ZM569-570Z"/>
</svg><span>Select by area</span></span>`

/**
 * Web component for rendering an entity list item.
 * Accepts a feature object as a JSON attribute and renders a list item with form fields.
 *
 * Usage:
 *   <entity-list-item
 *     feature='{"properties": {"_id": "123", "name": "Tree 1"}, "editableProperties": {...}}'
 *     job-id="job-123">
 *   </entity-list-item>
 */

/**
 * Whether a URL value can be exposed as a clickable "Open current link" anchor.
 * DOMPurify.isValidAttribute blocks XSS-bearing schemes (javascript:, data:, vbscript:, …);
 * the http(s) regex additionally restricts to navigable web URLs so bare paths like
 * "document.pdf" (which the LLM sometimes emits) don't render as a misleading link.
 * @param {string} value
 * @returns {boolean}
 */
const isOpenableUrl = (value) =>
  /^https?:\/\//i.test(value) && DOMPurify.isValidAttribute('a', 'href', value)

export class EntityListItem extends HTMLElement {
  static get observedAttributes () {
    return ['feature']
  }

  connectedCallback () {
    this.#render()
  }

  attributeChangedCallback () {
    this.#render()
  }

  /**
   * Formats a field name for display (replace underscores with spaces, capitalize)
   * @param {string} key
   * @returns {string}
   */
  #formatFieldName (key) {
    return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  /**
   * Formats a date value for input (yyyy-mm-dd)
   * @param {string | null} value
   * @returns {string}
   */
  #formatDateValue (value) {
    if (!value) return ''
    // If already in yyyy-mm-dd format, return as is
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value
    }
    // Try to parse and format date
    try {
      const date = new Date(value)
      if (isNaN(date.getTime())) return ''
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    } catch {
      return ''
    }
  }

  /**
   * Renders a form field based on the key and value
   * @param {string} key
   * @param {unknown} value
   * @returns {ReturnType<typeof html>}
   */
  #renderFormField (key, value) {
    const name = this.#formatFieldName(key)
    const fieldId = key
    const fieldName = key
    const fieldValue = value || ''

    // Determine input type
    let inputType = 'text'
    if (key.endsWith('_date')) {
      inputType = 'date'
    } else if (key === 'notes' || key === 'description') {
      inputType = 'textarea'
    } else if (key.endsWith('_url')) {
      inputType = 'url'
    }

    // Handle textarea
    if (inputType === 'textarea') {
      return html`
        <div class="govuk-form-group">
          <label class="govuk-label govuk-fieldset__legend--s" for="${fieldId}">
            ${name}
          </label>
          <textarea
            class="govuk-textarea"
            id="${fieldId}"
            name="${fieldName}"
            rows="5"
          >${String(fieldValue)}</textarea>
        </div>
      `
    }

    // Handle date input
    if (inputType === 'date') {
      const dateValue = this.#formatDateValue(typeof value === 'string' ? value : value === null ? null : String(value))
      return html`
        <div class="govuk-form-group">
          <label class="govuk-label govuk-fieldset__legend--s" for="${fieldId}">
            ${name}
          </label>
          <input
            class="govuk-input"
            type="date"
            id="${fieldId}"
            name="${fieldName}"
            value="${dateValue}"
          />
        </div>
      `
    }

    // Handle URL input (any allowed field whose key ends in `_url`, e.g. document_url, documentation_url)
    if (inputType === 'url') {
      const value = typeof fieldValue === 'string' ? fieldValue : ''
      // The "Open current link" anchor mirrors the input's live value: #handleUrlInput
      // updates href / visibility / the screen-reader-only URL on every keystroke so
      // the link always points to what's currently in the box, not the saved-at-render
      // value. DOMPurify.isValidAttribute is used both here at render and in the input
      // handler to gate the href — http(s) URLs (and other DOMPurify-allowlisted
      // schemes) pass through, javascript: / data: / etc. are blocked and the link
      // hides. LLM-extracted values are sometimes bare paths like "document.pdf";
      // those don't fail the DOMPurify check but won't navigate as expected, so we
      // additionally hide the link for non-http(s) values via the same regex used
      // server-side.
      const safeHref = isOpenableUrl(value) ? value : ''
      return html`
        <div class="govuk-form-group">
          <label class="govuk-label govuk-fieldset__legend--s" for="${fieldId}">
            ${name}
          </label>
          <div class="govuk-hint" data-url-opener-hint style="${safeHref ? '' : 'display: none'}">
            <a class="govuk-link" data-url-opener href="${safeHref}" target="_blank" rel="noopener noreferrer">Open current link<span class="govuk-visually-hidden" data-url-opener-sr>: ${safeHref} (opens in new window)</span></a>
          </div>
          <input
            class="govuk-input"
            type="url"
            id="${fieldId}"
            name="${fieldName}"
            value="${value}"
            oninput=${this.#handleUrlInput}
          />
        </div>
      `
    }

    // Handle text input
    if (key === 'permitted_development_rights') {
      return html`
        <div class="govuk-form-group">
          <label class="govuk-label govuk-fieldset__legend--s" for="${fieldId}">
            ${name}
          </label>
          <div class="govuk-hint">
            A comma-separated list of <a class="govuk-link" href="https://www.planning.data.gov.uk/dataset/permitted-development-right" target="_blank" rel="noopener noreferrer">PDR codes <span class="govuk-visually-hidden">(opens in new window)</span></a>
          </div>
          <input
            class="govuk-input"
            type="text"
            id="${fieldId}"
            name="${fieldName}"
            value="${String(fieldValue)}"
          />
        </div>
      `
    }

    return html`
      <div class="govuk-form-group">
        <label class="govuk-label govuk-fieldset__legend--s" for="${fieldId}">
          ${name}
        </label>
        <input
          class="govuk-input"
          type="text"
          id="${fieldId}"
          name="${fieldName}"
          value="${String(fieldValue)}"
        />
      </div>
    `
  }

  /**
   * Handles click on details element to select the feature
   * @param {Event} evt
   */
  #handleClick = (evt) => {
    const details = evt.currentTarget
    if (!(details instanceof HTMLElement)) return

    // Don't trigger feature selection if clicking on form elements
    const target = evt.target
    if (!(target instanceof HTMLElement)) return

    // Check if the click target is a form element or within a form
    const formElements = ['LABEL', 'INPUT', 'TEXTAREA', 'BUTTON', 'SELECT']
    if (formElements.includes(target.tagName) || target.closest('form')) {
      return
    }

    const entityId = details.getAttribute('data-entity-id')
    if (entityId) {
      reactiveStore.selectedFeatureId.set(entityId)
    }
  }

  /**
   * Handles blur/change and updates the in-memory feature properties.
   * Persistence is handled by the feature autosave subscriber.
   * @param {Event} evt
   */
  #handleFieldUpdate = (evt) => {
    const target = evt.target
    if (!(target instanceof HTMLElement)) return
    if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement)) {
      return
    }

    const form = target.closest('form')
    if (!(form instanceof HTMLFormElement) || !form.dataset.entityId) {
      return
    }

    const formData = new FormData(form)
    const editableProperties = {}
    for (const [key, value] of formData.entries()) {
      if (key !== 'extraction_result_id' && key !== 'action') {
        editableProperties[key] = String(value)
      }
    }

    const currentFeatures = reactiveStore.features.get()
    const entityId = form.dataset.entityId

    if (this.hasAttribute('is-metadata')) {
      // Tab 1 (TPO/Article 4 metadata) - write to jobData
      const currentJobData = reactiveStore.jobData.get()
      reactiveStore.jobData.set({ ...currentJobData, ...editableProperties })
    } else {
      // Tabs 2-4 (Trees, Zones) - write to features (existing behaviour)
      const updatedFeatures = currentFeatures.map(feature => {
        if (feature.properties?._id === entityId) {
          return {
            ...feature,
            properties: {
              ...feature.properties,
              ...editableProperties
            }
          }
        }
        return feature
      })

      reactiveStore.features.set(updatedFeatures)
    }
  }

  /**
   * Updates the adjacent "Open current link" anchor on every keystroke so the
   * link always points at what the user has typed. We re-run isOpenableUrl
   * (DOMPurify + http(s) regex) per keystroke — cheap, and means a half-typed
   * or dangerous value can never end up on the live href.
   * @param {Event} evt
   */
  #handleUrlInput = (evt) => {
    const input = evt.currentTarget
    if (!(input instanceof HTMLInputElement)) return
    const formGroup = input.closest('.govuk-form-group')
    if (!formGroup) return
    const hint = formGroup.querySelector('[data-url-opener-hint]')
    const link = formGroup.querySelector('[data-url-opener]')
    const srOnly = formGroup.querySelector('[data-url-opener-sr]')
    if (!(hint instanceof HTMLElement) || !(link instanceof HTMLAnchorElement) || !(srOnly instanceof HTMLElement)) return

    const value = input.value
    if (isOpenableUrl(value)) {
      link.href = value
      srOnly.textContent = `: ${value} (opens in new window)`
      hint.style.display = ''
    } else {
      hint.style.display = 'none'
      // Clear the href so a stale value can't be activated via keyboard while hidden.
      link.removeAttribute('href')
      srOnly.textContent = ''
    }
  }

  /**
   * Handles form submission for "Mark as complete" action.
   * @param {SubmitEvent} evt
   */
  #handleFormSubmit = async (evt) => {
    const form = evt.target
    if (!(form instanceof HTMLFormElement) || !form.dataset.entityId || !form.dataset.jobId) {
      return
    }

    evt.preventDefault()

    try {
      const formData = new FormData(form, evt.submitter)
      const urlSearchParams = new URLSearchParams()
      for (const [key, value] of formData.entries()) {
        urlSearchParams.append(key, String(value))
      }
      const response = await fetch(`/extractions/${form.dataset.jobId}/entity/${form.dataset.entityId}/properties`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: urlSearchParams
      })
      if (!response.ok) throw new Error('Failed to save form')

      // Update reactiveStore.features with the saved form data (only for non-metadata entities)
      if (!this.hasAttribute('is-metadata')) {
        const currentFeatures = reactiveStore.features.get()
        const entityId = form.dataset.entityId

        // Convert FormData to object, excluding non-editable fields
        const editableProperties = {}
        for (const [key, value] of formData.entries()) {
          if (key !== 'extraction_result_id' && key !== 'action') {
            editableProperties[key] = String(value)
          }
        }

        // Update the feature in the store
        const updatedFeatures = currentFeatures.map(feature => {
          if (feature.properties?._id === entityId) {
            return {
              ...feature,
              properties: {
                ...feature.properties,
                ...editableProperties, // Update properties with form values
                reviewed: 'Completed'
              }
            }
          }
          return feature
        })

        reactiveStore.features.set(updatedFeatures, 'no-save')
      }

      const details = form.closest('details')
      const tag = details?.querySelector('status-tag')
      if (tag) {
        tag.setAttribute('data-complete', 'true')
      }
      // Dispatch event to notify parent text-data component
      this.dispatchEvent(new CustomEvent('entity-saved', { bubbles: true }))
      // close details on successful save
      details?.removeAttribute('open')
      details?.focus()
    } catch (error) {
      alert('Failed to save form. Please try again.')
    }
  }

  /**
   * Renders the component
   */
  #render () {
    const featureAttr = this.getAttribute('feature')
    const jobId = this.getAttribute('job-id') || ''

    if (!featureAttr) {
      return
    }

    let feature
    try {
      feature = JSON.parse(featureAttr)
    } catch (error) {
      console.error('entity-list-item: Failed to parse feature attribute:', error)
      return
    }

    // Extract feature properties
    // Ensure metadata entities have _id in properties
    if (this.hasAttribute('is-metadata') && feature.properties && !feature.properties._id) {
      feature.properties._id = this.getAttribute('job-id') || 'main'
    }

    const entityId = feature.properties?._id || feature.id || 'main'
    const reviewed = feature.properties?.reviewed === true || feature.properties?.reviewed === 'Completed' || feature.reviewed === true
    const extractionResultId = window['extractionResultId'] || '' /* eslint-disable-line dot-notation */
    const entityIdForForm = entityId

    // Compute editableProperties on-demand using shared utility
    const jobType = window['jobType'] /* eslint-disable-line dot-notation */
    const isMetadata = this.hasAttribute('is-metadata')
    const entityType = getEntityType(jobType, feature, isMetadata)
    const editableProperties = getAllowedEntityPropertiesForEntity(feature, entityType)

    // Determine if we have editable properties
    const hasEditableProperties = editableProperties && Object.keys(editableProperties).length > 0

    // Determine delete button text based on feature type
    const featureType = feature.properties?.feature_type
    const deleteButtonText = featureType === 'tree' ? 'Delete tree' : featureType === 'tree_preservation_zone' ? 'Delete zone' : 'Delete area'

    // Build form action URL (base path is always '/')
    const formAction = `/extractions/${jobId}/entity/${entityIdForForm}/properties`

    const hasGeometry = !!feature.geometry?.coordinates?.length

    const noGeometryMessage = featureType === 'tree'
      ? html`The point for this feature is missing from the map. Please add using the ${ICON_ADD_POINT} tool`
      : html`The shape for this feature is missing from the map. Please add using either the ${ICON_ADD_FREEFORM} or ${ICON_MAGIC_WAND} tool`

    render(this, html`
        <details data-entity-id="${entityId}" onclick=${this.#handleClick}>
          <summary class="govuk-!-padding-top-3">
            <span class="govuk-body-s govuk-!-margin-bottom-3 govuk-!-padding-right-1">
              <span class="entity-list__carat govuk-!-display-inline-block" aria-hidden="true">&#9654;</span>
              <span class="entity-list__name govuk-!-padding-left-1">${editableProperties.name || feature.properties?.name}</span>
            </span>
            <span>
              <status-tag data-complete="${reviewed ? 'true' : 'false'}" data-has-geometry="${hasGeometry ? 'true' : 'false'}" data-feature-type="${featureType || ''}"></status-tag>
            </span>
          </summary>

          ${hasEditableProperties
? html`
            <form
              class="govuk-!-padding-top-3"
              action="${formAction}"
              data-job-id="${jobId}"
              data-entity-id="${entityIdForForm}"
              method="post"
              onchange=${this.#handleFieldUpdate}
              onsubmit=${this.#handleFormSubmit}>
              ${!hasGeometry && (featureType === 'tree' || featureType === 'tree_preservation_zone')
? html`
                <div class="no-geometry">
                  <svg aria-hidden="true" focusable="false" height="20px" viewBox="0 -960 960 960" width="20px"><path d="m40-120 440-760 440 760H40Zm138-80h604L480-720 178-200Zm330.5-51.5Q520-263 520-280t-11.5-28.5Q497-320 480-320t-28.5 11.5Q440-297 440-280t11.5 28.5Q463-240 480-240t28.5-11.5ZM440-360h80v-200h-80v200Zm40-100Z"/></svg>
                  <span>${noGeometryMessage}</span>
                </div>
`
: ''}
              <input type="hidden" name="extraction_result_id" value="${extractionResultId}">
              ${Object.entries(editableProperties)
                .filter(([key]) => key !== 'permitted_development_rights_details')
                .map(([key, value]) => this.#renderFormField(key, value))}
              <button class="govuk-button govuk-!-margin-bottom-2" name="action" value="complete" type="submit" formnovalidate>
                Mark as complete
              </button>
              
              <!-- Only show the delete button for features, i.e. not job metadata -->
              ${featureType
                ? html`
                <delete-feature entity-id="${entityId}" feature-type="${featureType}">
                  <button 
                    class="govuk-button govuk-button--warning govuk-!-margin-bottom-3" 
                    type="button">
                    ${deleteButtonText}
                  </button>
                </delete-feature>
              `
: ''}
            </form>
          `
: html`
            <p class="govuk-body-s govuk-!-margin-top-3">No properties were found for this entity.</p>
          `}
        </details>
    `)
  }
}

customElements.define('entity-list-item', EntityListItem)
